import { useRef, useState } from "react"
import { useAuth } from "../../contexts/AuthContext"
import { Button, Card, Input } from "../primitives"
import { useTranslation } from "../../hooks/useTranslation";
import useTimedError from "../../hooks/useTimedError";
import { catchErrorAuth } from "../../ErrorUtility";
import serviceIcons from "../../assets/service-icons";
import { useDatabase } from "../../contexts/DatabaseContext";

export default function NewPassword({ setIsOpen }) {
    const passwordRef = useRef()
    const { updateCurrentUserPassword } = useAuth()
    const t = useTranslation();
    const [error, setError] = useTimedError();
    const [loading, setLoading] = useState(false);
    const [passwordComplexityVisible, setPasswordComplexityVisible] = useState(false);
    const [pass, setPass] = useState("");
    const [success, setSuccess] = useState(null);
    const { createAccountActionEvent } = useDatabase();

    const [passwordComplexity, setPasswordComplexity] = useState([
        {
            name: "length",
            value: false,
            message: t("At Least 8 Characters"),
            error: t("Password must be at least 8 characters long")
        },
        {
            name: "uppercase",
            value: false,
            message: t("At Least One Uppercase Letter"),
            error: t("Password must contain at least one uppercase letter")
        },
        {
            name: "lowercase",
            value: false,
            message: t("At Least One Lowercase Letter"),
            error: t("Password must contain at least one lowercase letter")
        },
        {
            name: "number",
            value: false,
            message: t("At Least One Number"),
            error: t("Password must contain at least one number")
        }
    ]);

    const handlePassChange = (event) => {
        setPass(event.target.value);
        setPasswordComplexity(passwordComplexity.map((complexity) => ({
            ...complexity,
            value: (() => {
                switch (complexity.name) {
                    case "length":
                        return event.target.value.length >= 8;
                    case "uppercase":
                        return /[A-Z]/.test(event.target.value);
                    case "lowercase":
                        return /[a-z]/.test(event.target.value);
                    case "number":
                        return /\d/.test(event.target.value);
                    default:
                        return false;
                }
            })()
        })));
    };

    async function updatePassword(e) {
        e.preventDefault();
        setLoading(true);
        
        try {
            passwordComplexity.forEach((complexity) => {
                if (!complexity.value) {
                    throw new Error(complexity.error);
                }
            });
        } catch (error) {
            setError(error.message);
            setLoading(false);
            return;
        }
        
        const [result, errorCode, errorMessage] = await catchErrorAuth(updateCurrentUserPassword, passwordRef.current.value);
        if (errorCode) {
            setError(errorMessage);
            setLoading(false);
        } else {
            setLoading(false);
            setSuccess(t('Password updated!'));
            setTimeout(() => {
                console.log("closing");
                setIsOpen(false);
            }, 2000);
            await createAccountActionEvent("update_credentials");
        }
    }

    return (
        <Card ctype="white">
            <Card.Header>
                <h2>{t('Update Password')}</h2>
            </Card.Header>
            <Card.Body>
                <form onSubmit={updatePassword}>
                    <Input 
                        required 
                        type="password" 
                        ref={passwordRef} 
                        value={pass}
                        labelText={t('Password')} 
                        onChange={handlePassChange}
                        onFocus={() => setPasswordComplexityVisible(true)}
                    />
                    {passwordComplexityVisible && <div className="password-complexity mv-15">
                        {passwordComplexity.map((complexity) => (
                            <div key={complexity.name} className={`password-complexity-item ${complexity.value ? "valid" : "invalid"} entering`}>
                                {complexity.value ? <img src={serviceIcons["CheckMark.svg"]} alt="Check Mark" className="password-complexity-icon valid" /> : <img src={serviceIcons["XMark.svg"]} alt="X Mark" className="password-complexity-icon invalid" />}
                                <span className="password-complexity-message">{complexity.message}</span>
                            </div>
                        ))}
                    </div>}
                    <Button buttonType="submit" loadingFinish={success} error={error} loading={loading}>
                        {error || success || t('Update')}
                    </Button>
                </form>
            </Card.Body>
        </Card>
    )
}